import { type BaseState, type GameDefinition, type MoveOutcome, reject } from "../types";

/**
 * A race decided by rhythm rather than speed. A beat lands every so often; tap
 * near it and your snail hops, tap wildly and it stalls.
 *
 * Both players act at once, so there is no `turn` — the beat is the clock, and
 * `tick` closes each one. That keeps it to roughly one message a second per
 * player rather than the continuous stream a real-time game would need.
 */

export const DASH_TRACK = 12;
/** Gap between beats. It shortens as the race goes on. */
export const DASH_FIRST_BEAT_MS = 1400;
export const DASH_LAST_BEAT_MS = 700;
/** How close to the beat a tap has to be to count. */
export const DASH_WINDOW_MS = 260;
/** Beats before the race is called on whoever is further along. */
export const DASH_MAX_BEATS = 40;

export interface SnailDashState extends BaseState {
	kind: "snaildash";
	/** How far along the track each snail is. */
	position: [number, number];
	/** Beats hit in a row, which is what makes a hop worth two. */
	streak: [number, number];
	/** When the beat currently being played lands. */
	beatAt: number;
	beatNumber: number;
	/** Whether each player has already tapped for this beat. */
	tapped: [boolean, boolean];
	/** What happened to each player on the beat just gone, for the board. */
	lastBeat: [DashOutcome, DashOutcome];
	/**
	 * Seeds the computer snail's per-beat hit-or-miss rolls, so a re-polled
	 * room always gets the same answer for the same beat but every race rolls
	 * differently. Redacted — holding it would tell you exactly which beats
	 * the computer is going to fluff.
	 */
	rollSeed: number;
}

export type DashOutcome = "none" | "hit" | "miss";

export interface SnailDashMove {
	/** Only ever "tap" — the timing is taken from the server's clock, not sent. */
	tap: true;
}

/** Beats speed up smoothly across the race, so the ending is the hard part. */
export function beatGap(beatNumber: number): number {
	const progress = Math.min(1, beatNumber / DASH_MAX_BEATS);
	return Math.round(DASH_FIRST_BEAT_MS + (DASH_LAST_BEAT_MS - DASH_FIRST_BEAT_MS) * progress);
}

function leader(position: [number, number]): 0 | 1 | null {
	if (position[0] === position[1]) return null;
	return position[0] > position[1] ? 0 : 1;
}

export const snailDash: GameDefinition<SnailDashState, SnailDashMove> = {
	id: "snaildash",
	category: "racing",
	title: "Snail Dash",
	emoji: "🐌",
	blurb: "Tap on the beat to hop forward. Miss it and your snail stalls.",
	howTo: [
		"A beat plays over and over, getting faster as the race goes on.",
		"Tap right as the beat lands and your snail hops forward.",
		"Hit several beats in a row and you start hopping twice as far.",
	],

	create(rng, now) {
		return {
			kind: "snaildash",
			position: [0, 0],
			streak: [0, 0],
			beatAt: now + DASH_FIRST_BEAT_MS,
			beatNumber: 0,
			tapped: [false, false],
			lastBeat: ["none", "none"],
			rollSeed: rng.int(0x7fffffff),
			turn: null,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		return (raw as { tap?: unknown }).tap === true ? { tap: true } : null;
	},

	apply(state, player, _move, now): MoveOutcome<SnailDashState> {
		if (state.result) return reject("This race is already finished.");
		if (state.tapped[player]) return reject("You've already tapped for this beat.");

		/*
		 * A tap more than a window before the beat has not happened yet.
		 *
		 * It used to be judged anyway — against a beat still most of a second away
		 * — which charged it to that beat as a miss *and* spent the player's one
		 * tap on it, so a single eager tap cost two beats and the streak. Kids
		 * drum, and a rejection here would spam them with errors for it, so the
		 * drum-roll is simply let through as a no-op instead.
		 */
		if (now < state.beatAt - DASH_WINDOW_MS) return { ok: true, state };

		const tapped: [boolean, boolean] = [...state.tapped];
		tapped[player] = true;

		const offBy = Math.abs(now - state.beatAt);
		const onBeat = offBy <= DASH_WINDOW_MS;

		const position: [number, number] = [...state.position];
		const streak: [number, number] = [...state.streak];
		const lastBeat: [DashOutcome, DashOutcome] = [...state.lastBeat];

		if (onBeat) {
			streak[player] += 1;
			// Three in a row and the snail really gets going.
			position[player] += streak[player] >= 3 ? 2 : 1;
			lastBeat[player] = "hit";
		} else {
			streak[player] = 0;
			lastBeat[player] = "miss";
		}

		const next: SnailDashState = {
			...state,
			position,
			streak,
			tapped,
			lastBeat,
			scores: position,
		};

		if (position[player] >= DASH_TRACK) {
			return { ok: true, state: { ...next, result: { winner: player } } };
		}
		return { ok: true, state: next };
	},

	legalMoves(state, player) {
		// Tapping is always allowed, once per beat — that is the whole game.
		if (state.result || state.tapped[player]) return [];
		return [{ tap: true }];
	},

	/**
	 * Closes the beat: anyone who never tapped has missed it, and the next beat
	 * is scheduled. This is the only place the race moves on by itself.
	 */
	tick(state, now) {
		if (state.result) return null;
		if (now < state.beatAt + DASH_WINDOW_MS) return null;

		const streak: [number, number] = [...state.streak];
		const lastBeat: [DashOutcome, DashOutcome] = [...state.lastBeat];
		for (const player of [0, 1] as const) {
			if (!state.tapped[player]) {
				streak[player] = 0;
				lastBeat[player] = "miss";
			}
		}

		const beatNumber = state.beatNumber + 1;
		const next: SnailDashState = {
			...state,
			streak,
			lastBeat,
			beatNumber,
			tapped: [false, false],
			beatAt: now + beatGap(beatNumber),
		};

		// Somebody has to win: after enough beats, whoever is further along does.
		if (beatNumber >= DASH_MAX_BEATS) {
			return { ...next, result: { winner: leader(state.position) } };
		}
		return next;
	},

	nextTickAt(state) {
		if (state.result) return null;
		return state.beatAt + DASH_WINDOW_MS;
	},

	redact(state) {
		// The roll seed is the computer's future mistakes, so it stays server-side.
		return { ...state, rollSeed: 0 };
	},
};
